import { Fragment, memo, useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Collapse, { Panel } from "rc-collapse";
import BookingDurationList from "app/pages/PrePackage/Duration/BookingDurationList.jsx";
import BookingAccommodationList from "app/pages/PrePackage/rental/BookingAccommodationList.jsx";
import Loader from "app/pages/.shared/Loader.jsx";
import DurationLabel from "app/pages/.shared/DurationLabel";
import Period from "app/pages/.shared/Period.jsx";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import { scrollToElement } from "app/utils/scroller";
import findIndex from "lodash/findIndex";
import { FormattedMessage, useIntl } from "react-intl";
import { propTypes, untouch } from "redux-form";
import { RESOLUTION, STRATEGY } from "app/constants";
import "app/pages/PrePackage/components/SearchEngine.scss";

import Button from "app/pages/.shared/form/Button";
import {
	pushTagOnDepartureDateSelect,
	pushTagOnDurationSelect,
	pushTagOnRentalAccommodationSelect,
} from "app/utils/analytics";
import AvailableDurations from "app/pages/.shared/AvailableDurations";
import "../components/SearchEngine.scss";
import classNames from "classnames";
import SearchEngineMessagePlaceholderContainer from "app/pages/PrePackage/SearchEngineMessagePlaceholder/SearchEngineMessagePlaceholderContainer";
import Modal from "react-modal";
import AuthLayerMobile from "app/pages/Auth/AuthLayerMobile/AuthLayerMobile";
import AuthModal from "app/pages/Auth/AuthModal/AuthModal";
import { resolutionPropType, strategyShape } from "app/utils/propTypes";
import loadable from "@loadable/component";
import { useLocation } from "react-router-dom";
import { arrow, FloatingArrow, offset, shift, useFloating } from "@floating-ui/react";
import { anyFieldTouched } from "app/utils/form-validator";

const DepartureDatePicker = loadable(() =>
	import(/* webpackChunkName: "datepicker" */ "app/pages/PrePackage/DepartureDate/DepartureDatePicker.jsx")
);

const ACCORDION = {
	DURATIONS_DATES_PANEL_KEY: "0",
	ACCOMMODATIONS_PANEL_KEY: "1",
	DEPARTURE_DATES_PANEL_KEY: "2",
};

const mobileModalStyle = {
	overlay: {
		zIndex: 6,
	},
	content: {
		top: "0",
		left: "0",
		right: "0",
		bottom: "0",
		border: "none",
		background: "none",
		overflow: "hidden",
	},
};

const RentalSearchEngine = props => {
	const {
		resolution,
		fields,
		prePackageOffers = [],
		submitting,
		allAvailableDurations,
		rentalAccommodations = [],
		durations = [],
		prices = [],
		months = [],
		booking,
		error, // set to undefined on each redux-form/CHANGE
		isDealExpired,
		home = {},
		updateBooking = () => {},
		handleSubmit,
		strategy,
		uuid,
	} = props;
	const intl = useIntl();
	const durationRef = useRef(null);
	const rentalAccommodationRef = useRef(null);
	const departureDateRef = useRef(null);
	const offerRef = useRef(null);
	const searchEngineDurationRef = useRef(null);
	const arrowRef = useRef(null);

	const [authModalVisible, setAuthModalVisible] = useState(false);
	const [isOpen, setIsOpen] = useState(false);

	const location = useLocation();

	const selectedDepartureDateValue = booking?.departureDate;
	const selectedAccommodationValue = booking?.rentalAccommodation?.accommodation;
	const selectedDurationValue = booking?.duration?.value;

	// Par défaut, on veut fermer tous les volets du moteur de recherche si tous les options ont été sélectionnées
	// Sinon on ouvre le volet des durées par défaut
	const [accordionActiveKey, setAccordionActiveKey] = useState(
		selectedAccommodationValue && selectedDepartureDateValue && selectedDurationValue
			? -1
			: ACCORDION.DURATIONS_DATES_PANEL_KEY
	);

	const closeAuthModal = () => {
		setAuthModalVisible(false);
	};

	const onDurationSelection = durationValue => {
		const duration = durations.find(duration => duration?.value === durationValue);
		// On reset la selection de la chambre et de la date de depart quand on change de durée pour
		// éviter d'avoir un loader infini dans le calendrier de prix.
		// A voir plus tard pour ne reset que si la selection n'existe pas dans la liste des chambre
		updateBooking({ duration, rentalAccommodation: {}, departureDate: undefined });
		fields.rentalAccommodation.onChange("");
		fields.departureDate.onChange("");
		setAccordionActiveKey(ACCORDION.ACCOMMODATIONS_PANEL_KEY);

		const offset = location.pathname === "/booking/search" ? -28 : -75;
		scrollToElement({ element: durationRef.current, offset });

		pushTagOnDurationSelect(duration?.value);
	};

	const onRentalAccommodationSelection = useCallback(
		rentalAccommodation => {
			updateBooking({
				rentalAccommodation,
				config: rentalAccommodation.config,
			});
			setAccordionActiveKey(ACCORDION.DEPARTURE_DATES_PANEL_KEY);

			const offset = location.pathname === "/booking/search" ? -28 : -75;
			scrollToElement({ element: durationRef.current, offset });

			pushTagOnRentalAccommodationSelect();
		},
		[durationRef?.current]
	);

	const onPriceSelection = useCallback(
		price => {
			const departureDate = price.dd;
			const endDate = price.ed;

			updateBooking({
				departureDate: departureDate,
				endDate: endDate,
				price: price.p,
				publicPrice: price.pp,
			});
			setAccordionActiveKey(undefined);

			const offset = location.pathname === "/booking/search" ? -28 : -75;
			scrollToElement({ element: durationRef.current, offset }); // scroll pour afficher tout le moteur de recherche

			pushTagOnDepartureDateSelect(departureDate, endDate);
		},
		[durationRef?.current]
	);

	const changeAccordionActivePanel = useCallback(activeKey => {
		setAccordionActiveKey(activeKey);
	}, []);

	useEffect(() => {
		if (anyFieldTouched(fields) && error) {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
	}, [fields, error]);

	useEffect(() => {
		let defaultAccordionActiveKey;

		if (!selectedDurationValue) {
			defaultAccordionActiveKey = ACCORDION.DURATIONS_DATES_PANEL_KEY;
		} else if (!selectedAccommodationValue) {
			defaultAccordionActiveKey = ACCORDION.ACCOMMODATIONS_PANEL_KEY;
		} else if (!selectedDepartureDateValue) {
			defaultAccordionActiveKey = ACCORDION.DEPARTURE_DATES_PANEL_KEY;
		} else {
			/* returning here to avoid the accordion to collapse when no criteria are met */
			return;
		}
		setAccordionActiveKey(defaultAccordionActiveKey);
	}, [selectedDurationValue, selectedAccommodationValue, selectedDepartureDateValue]);

	const handleFormSubmit = useCallback(
		(...args) => {
			if (strategy === STRATEGY.PRODUCT_FIRST && !uuid) {
				const [event] = args;
				// note: ici on fait un event.preventDefault() nous même afin d'éviter le rechargement de la page
				event.preventDefault();
				return setAuthModalVisible(true);
			}
			return handleSubmit(...args);
		},
		[strategy, uuid]
	);

	const bestPriceIndex = findIndex(prices, price => price.i && price.i.mp === true);
	const initialIndex = selectedDepartureDateValue
		? findIndex(prices, price => price.dd === selectedDepartureDateValue)
		: bestPriceIndex;

	const searchEngineDepartureDateNode =
		prices.length !== 0 ? (
			<div className="search-engine__price">
				<DepartureDatePicker
					isRental={true}
					initialIndex={initialIndex}
					months={months}
					prices={prices}
					field={fields.departureDate}
					onPriceChange={onPriceSelection}
				/>
			</div>
		) : (
			<div className="search-engine__loader">
				<Loader />
			</div>
		);

	const durationHeaderLabel =
		selectedDurationValue && accordionActiveKey !== ACCORDION.DURATIONS_DATES_PANEL_KEY ? (
			<div className="search-engine__header" ref={durationRef}>
				<div className="search-engine__label">
					<DurationLabel duration={selectedDurationValue} />
				</div>
				<div className="search-engine__edit">
					<FormattedMessage id="search.engine.edit.label" />
				</div>
			</div>
		) : (
			<div className="search-engine__header" ref={durationRef}>
				<FormattedMessage id="search.engine.durations.title" />
			</div>
		);

	const rentalAccommodationHeaderLabel =
		selectedAccommodationValue && accordionActiveKey !== ACCORDION.ACCOMMODATIONS_PANEL_KEY ? (
			<div className="search-engine__header" ref={rentalAccommodationRef}>
				<div className="search-engine__label">{selectedAccommodationValue}</div>
				<div className="search-engine__edit">
					<FormattedMessage id="search.engine.edit.label" />
				</div>
			</div>
		) : (
			<div className="search-engine__header" ref={rentalAccommodationRef}>
				<FormattedMessage id="search.engine.rental.accommodation.title" />
			</div>
		);

	const departureDateHeaderLabel =
		selectedDepartureDateValue && accordionActiveKey !== ACCORDION.DEPARTURE_DATES_PANEL_KEY ? (
			<div className="search-engine__header" ref={departureDateRef}>
				<div className="search-engine__label">
					{selectedDepartureDateValue ? (
						<Period
							prefix={<FormattedMessage id="general.period.prefix" />}
							suffix={<FormattedMessage id="general.period.suffix" />}
							fromDate={selectedDepartureDateValue}
							endDate={booking.endDate}
							format={{
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							}}
						/>
					) : (
						false
					)}
				</div>
				<div className="search-engine__edit">
					<FormattedMessage id="search.engine.edit.label" />
				</div>
			</div>
		) : (
			<div className="search-engine__header" ref={departureDateRef}>
				<FormattedMessage id="search.engine.departure.date.title" />
			</div>
		);

	const offerClassName = classNames("search-engine__offer", {
		"rc-collapse-item--disabled-filter": isDealExpired,
	});

	const rentalAccommodationClassName = classNames("cy-search-engine-accommodation-panel", {
		"rc-collapse-item-hidden": !selectedDurationValue,
	});

	const durationClassName = classNames("cy-search-engine-duration-panel", {
		"rc-collapse-item-disabled": isDealExpired,
		"rc-collapse-item-hidden": isDealExpired,
	});

	const departureDateAccommodationClassName = selectedAccommodationValue
		? ""
		: "rc-collapse-item-hidden";

	const footerClassName = classNames("search-engine__footer", {
		"search-engine__footer--hidden": !selectedDepartureDateValue,
	});

	const errorMessage = error ? intl.formatMessage(error) : "";

	const onMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const handleClose = useCallback(() => {
		setIsOpen(false);
		untouch(
			"booking-search-form",
			"offer",
			"departureCityCode",
			"departureDate",
			"duration",
			"adults",
			"children",
			"infants"
		);
	}, []);

	const { context, y, x, refs, strategy: floatingStrategy } = useFloating({
		placement: "top",
		strategy: "absolute",
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [
			offset(),
			shift(),
			arrow({
				element: arrowRef,
			}),
		],
	});

	return (
		<Fragment>
			<Modal isOpen={authModalVisible && onMobile} style={mobileModalStyle}>
				<AuthLayerMobile onAuthSuccess={closeAuthModal} />
			</Modal>
			<AuthModal
				home={home}
				isOpen={authModalVisible && !onMobile}
				onAuthSuccess={closeAuthModal}
			/>
			<form
				id="search-engine"
				className="search-engine search-engine--rental"
				onSubmit={handleFormSubmit}
			>
				<div className={offerClassName} ref={offerRef}>
					<div className="rental-offer">
						<div className="rental-offer__header">
							<div className="rental-offer__type-label">
								<FormattedMessage id="search.engine.rental.offer.label" />:
							</div>
							<div className="rental-offer__type-value">
								<FormattedMessage id="search.engine.rental.without.transport.label" />
							</div>
						</div>

						{prePackageOffers[0] ? (
							<div>
								<div className="rental-offer__price rental-offer__price--final">
									<AmountContainer
										prefix={
											<FormattedMessage id="offer.per.person.from.label" />
										}
										amount={prePackageOffers[0].pricing.minPrice.p}
										suffix={
											<FormattedMessage id="general.amount.per.accommodation.suffix.label" />
										}
									/>
								</div>

								{prePackageOffers[0].pricing.minPrice.pp ? (
									<div className="rental-offer__price rental-offer__price--public">
										<AmountContainer
											amount={prePackageOffers[0].pricing.minPrice.pp}
											prefix={
												<FormattedMessage id="general.instead.of.price" />
											}
										/>
									</div>
								) : (
									false
								)}
							</div>
						) : (
							false
						)}

						{allAvailableDurations.length > 0 ? (
							<div className="rental-offer__available-durations">
								<AvailableDurations durations={allAvailableDurations} />
							</div>
						) : (
							false
						)}
					</div>
				</div>

				<Collapse
					accordion={true}
					onChange={changeAccordionActivePanel}
					activeKey={accordionActiveKey}
				>
					<Panel header={durationHeaderLabel} className={durationClassName}>
						<div className="search-engine__duration" ref={searchEngineDurationRef}>
							<BookingDurationList
								amountSuffix={
									<FormattedMessage id="general.amount.per.accommodation.suffix.label.short" />
								}
								durations={durations}
								field={fields.duration}
								onDurationChange={onDurationSelection}
							/>
						</div>
					</Panel>

					<Panel
						header={rentalAccommodationHeaderLabel}
						className={rentalAccommodationClassName}
					>
						<div className="search-engine__rental-accommodation">
							<BookingAccommodationList
								rentalAccommodations={rentalAccommodations}
								field={fields.rentalAccommodation}
								onRentalAccommodationChange={onRentalAccommodationSelection}
							/>
						</div>
					</Panel>

					<Panel
						header={departureDateHeaderLabel}
						className={departureDateAccommodationClassName}
					>
						<div className="search-engine__departure-date">
							{searchEngineDepartureDateNode}
						</div>
					</Panel>
				</Collapse>

				<div className={footerClassName}>
					{booking?.price ? (
						<div className="search-engine__selected-price">
							<AmountContainer
								amount={booking?.price}
								suffix={
									<FormattedMessage id="general.amount.per.accommodation.suffix.label" />
								}
							/>
						</div>
					) : (
						false
					)}

					{booking?.publicPrice ? (
						<div className="search-engine__selected-price search-engine__selected-price--public">
							<AmountContainer
								amount={booking?.publicPrice}
								prefix={<FormattedMessage id="general.instead.of.price" />}
							/>
						</div>
					) : (
						false
					)}

					<div className="search-engine__action-button" ref={refs.setReference}>
						<Button
							submit
							variant="primary"
							loading={submitting}
							data-testid="search-engine-button"
						>
							<FormattedMessage id="search.engine.book.now" />
						</Button>
						{isOpen && (
							<div
								className="search-engine__popover"
								ref={refs.setFloating}
								style={{
									position: floatingStrategy,
									top: y + 5 ?? "",
									left: x ?? "",
								}}
							>
								<FloatingArrow
									ref={arrowRef}
									context={context}
									className="search-engine__popover-arrow"
								/>
								<div className="search-engine__popover-content">
									<div className="search-engine__popover-text">
										{errorMessage}
									</div>
									<div
										className="search-engine__popover-close"
										onClick={handleClose}
									>
										<i className="icon icon--cross" />
									</div>
								</div>
							</div>
						)}

						<div className="search-engine__marketing-placeholder">
							<SearchEngineMessagePlaceholderContainer />
						</div>
					</div>
				</div>
			</form>
		</Fragment>
	);
};

RentalSearchEngine.propTypes = {
	...propTypes,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	durations: PropTypes.array,
	prices: PropTypes.array,
	months: PropTypes.array,
	resolution: resolutionPropType,
	booking: PropTypes.object,
	updateBooking: PropTypes.func,
	handleSubmit: PropTypes.func,
	strategy: strategyShape,
	isDealExpired: PropTypes.bool,
	home: PropTypes.object,
};

export default memo(RentalSearchEngine);
